import { Currency, ReviewStatus } from '../../constants';

export type ReviewMediaType = 'image' | 'video';

/** `pictureUrls` carries only the images out of this list, so URL-to-`<img>` callers keep working. */
export type ReviewMedia = {
  url: string;
  type: ReviewMediaType;
};

export type Review = {
  productId: number;
  reviewId: number;
  name: string;
  email: string;
  rating: number;
  body: string;
  title: string;
  pictureUrls: string[];
  skinType: string[];
  skinConcern: string[];
  status: ReviewStatus;
  /** Unaffected by edits, so this — not `modifiedAt` — dates the card and orders the list. */
  createdAt?: string;
  modifiedAt: string;
  /** Only the product list populates these; absent means no badge, not `false`. */
  verifiedPurchaser?: boolean;
  repeatPurchaser?: boolean;
  /** Absent on responses written before media existed, which is why `pictureUrls` stays the fallback. */
  media?: ReviewMedia[];
};

/** Hook/domain query params (the wire requests mirror these shapes). */
export interface ProductReviewsQueryParams {
  productId: number;
  status?: ReviewStatus;
  locale?: string;
}

export interface CuratedReviewsQueryParams {
  locale?: string;
}

/** The public list is CDN-cached and carries no author identity, so this is the only way to find one's own. */
export interface MyReviewsQueryParams {
  productId?: number;
}

export interface ReviewCreditAmountsQueryParams {
  currency?: string;
}

export interface ReviewCreditSummaryQueryParams {
  currency?: string;
}

/** Comma-joined ids, as the endpoint answers a page's worth of cards in one call. */
export interface ReviewStatisticsQueryParams {
  productIds: string;
}

/** What the presigned-URL endpoint will sign for; anything else is refused there. */
export const REVIEW_MEDIA_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'video/mp4',
  'video/quicktime',
  'video/webm',
] as const;

export type ReviewMediaContentType = (typeof REVIEW_MEDIA_CONTENT_TYPES)[number];

export const REVIEW_MEDIA_MAX_COUNT = 5;

/**
 * What reviewers are told, and what an unencoded file is held to.
 */
export const REVIEW_MEDIA_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/** The server's ceiling, which a re-encoded clip is held to: the encode aims at 9MB and overshoots. */
export const REVIEW_MEDIA_SERVER_MAX_FILE_SIZE_BYTES = 12 * 1024 * 1024;

export const isReviewMediaContentType = (contentType: string): contentType is ReviewMediaContentType =>
  (REVIEW_MEDIA_CONTENT_TYPES as readonly string[]).includes(contentType);

/** Length, not size, is the control: ten seconds runs 10MB to 67MB depending on the camera. */
export const REVIEW_VIDEO_MAX_DURATION_SECONDS = 30;

export const isReviewVideoContentType = (contentType: string): boolean => contentType.startsWith('video/');

/** Validated against `REVIEWER_AGE_GROUPS` in Facade. Tokens, so copy can change; labels live in the form. */
export const REVIEW_AGE_GROUPS = ['under_25', '25_34', '35_44', '45_54', '55_60', 'over_60'] as const;

export type ReviewAgeGroup = (typeof REVIEW_AGE_GROUPS)[number];

export const REVIEW_GENDERS = ['female', 'male', 'non_binary'] as const;

export type ReviewGender = (typeof REVIEW_GENDERS)[number];

/** Mirrors `ANONYMOUS_REVIEWER_NAME` in Facade so the form can say what will be published. */
export const ANONYMOUS_REVIEWER_NAME = 'SkinSeoul Member';
/** Priced per currency by the server, so the amounts are never baked into the copy. */
export type ReviewCreditAmounts = {
  currency: Currency;
  fractionDigits: number;
  textPoints: number;
  textCreditAmount: number;
  mediaPoints: number;
  mediaCreditAmount: number;
};

/** `alreadyCredited` separates the reader already paid from the one who never bought — they are told different things. */
export type ReviewCreditEligibility = {
  productId: number;
  /** The completed order the verdict was read from; absent when no order qualified. */
  orderId?: string;
  sku?: string;
  creditType: string;
  eligible: boolean;
  alreadyCredited: boolean;
};

/** Account-wide, so it cannot say which product is outstanding — the surfaces advertise a cap, not a total. */
export type ReviewCreditSummary = {
  claimable: boolean;
  creditAmount: number;
  points: number;
  currency: Currency;
  fractionDigits: number;
};

export type ProductStatistic = {
  productId: number;
  averageRate: number;
  totalReviews: number;
};
